import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { PRESETS } from "../lib/exportPresets";

// Public Q&A detail page — renders one answer at /ask/:slug. The body is
// stored as Markdown; we render it with a tiny home-grown renderer that
// handles the subset we use (headings, paragraphs, lists, bold, code, links).
// Avoiding a full markdown lib because the bundle cost isn't worth it for the
// 5-6 features we actually use.

function renderMd(md) {
    if (!md) return [];
    const lines = md.replace(/\r\n/g, "\n").split("\n");
    const out = [];
    let listBuf = null;
    const flushList = () => { if (listBuf) { out.push(listBuf); listBuf = null; } };
    const inline = (s) => {
        // Order matters: code first (so we don't interpret * inside backticks),
        // then bold, then links.
        const parts = [];
        let rest = s;
        const tokenizers = [
            { re: /`([^`]+)`/, render: (m) => <code key={parts.length} className="bg-paper border border-ink/10 px-1 py-0.5 font-mono text-[0.9em]">{m[1]}</code> },
            { re: /\*\*([^*]+)\*\*/, render: (m) => <strong key={parts.length}>{m[1]}</strong> },
            { re: /\[([^\]]+)\]\(([^)]+)\)/, render: (m) => <a key={parts.length} href={m[2]} className="text-cobalt hover:underline" target={m[2].startsWith("http") ? "_blank" : undefined} rel={m[2].startsWith("http") ? "noreferrer noopener" : undefined}>{m[1]}</a> },
        ];
        while (rest.length) {
            let earliest = null;
            for (const t of tokenizers) {
                const m = rest.match(t.re);
                if (m && (earliest === null || m.index < earliest.idx)) earliest = { tokenizer: t, match: m, idx: m.index };
            }
            if (!earliest) { parts.push(rest); break; }
            if (earliest.idx > 0) parts.push(rest.slice(0, earliest.idx));
            parts.push(earliest.tokenizer.render(earliest.match));
            rest = rest.slice(earliest.idx + earliest.match[0].length);
        }
        return parts;
    };

    lines.forEach((line, i) => {
        if (line.startsWith("## ")) { flushList(); out.push(<h2 key={`h2-${i}`} className="font-display font-black tracking-tighter text-2xl mt-10 mb-3">{inline(line.slice(3))}</h2>); }
        else if (line.startsWith("### ")) { flushList(); out.push(<h3 key={`h3-${i}`} className="font-display font-bold tracking-tight text-lg mt-6 mb-2">{inline(line.slice(4))}</h3>); }
        else if (/^(\d+)\. /.test(line)) {
            if (!listBuf || listBuf.type !== "ol") { flushList(); listBuf = { type: "ol", items: [], key: i }; }
            listBuf.items.push(<li key={i} className="mb-1.5">{inline(line.replace(/^(\d+)\. /, ""))}</li>);
        } else if (/^- /.test(line)) {
            if (!listBuf || listBuf.type !== "ul") { flushList(); listBuf = { type: "ul", items: [], key: i }; }
            listBuf.items.push(<li key={i} className="mb-1.5">{inline(line.slice(2))}</li>);
        } else if (line.trim() === "") { flushList(); }
        else { flushList(); out.push(<p key={`p-${i}`} className="mt-4 leading-relaxed">{inline(line)}</p>); }
    });
    flushList();
    // Wrap collected lists
    return out.map((node) => {
        if (node && node.type === "ol") return <ol key={`ol-${node.key}`} className="list-decimal pl-6 mt-4 space-y-1">{node.items}</ol>;
        if (node && node.type === "ul") return <ul key={`ul-${node.key}`} className="list-disc pl-6 mt-4 space-y-1">{node.items}</ul>;
        return node;
    });
}

export default function AskDetail() {
    const { slug } = useParams();
    const [item, setItem] = useState(undefined); // undefined = loading, null = 404
    useEffect(() => {
        api.get(`/ask/${slug}`)
            .then(({ data }) => setItem(data))
            .catch(() => setItem(null));
    }, [slug]);

    if (item === null) return <Navigate to="/ask" replace />;
    if (item === undefined) return <article className="px-6 sm:px-12 py-12 max-w-3xl mx-auto"><div className="text-sm text-ink-muted">Loading…</div></article>;

    const preset = item.related_preset ? PRESETS.find((p) => p.id === item.related_preset) : null;
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
            "@type": "Question",
            "name": item.title,
            "acceptedAnswer": { "@type": "Answer", "text": item.summary || item.title },
        }],
    };

    return (
        <>
            <Helmet>
                <title>{`${item.title} — OpenCSV Answers`}</title>
                <meta name="description" content={item.summary} />
                <link rel="canonical" href={`https://opencsv.io/ask/${item.slug}`} />
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Helmet>
            <article className="px-6 sm:px-12 pt-10 pb-24 max-w-3xl mx-auto">
                <Link to="/ask" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-ink-muted hover:text-ink ui-heading mb-6" data-testid="ask-back-link"><ArrowLeft size={12} /> All answers</Link>

                <div className="ui-heading text-xs uppercase tracking-widest text-cobalt">/ ask</div>
                <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-2 leading-[1.05]">{item.title}</h1>
                {item.summary && <p className="text-base sm:text-lg text-ink-soft mt-4 leading-relaxed">{item.summary}</p>}

                <div className="mt-6 text-ink prose-tight">{renderMd(item.body_md)}</div>

                {preset && (
                    <div className="mt-10 border border-cobalt/30 bg-cobalt/5 p-5" data-testid="ask-preset-cta">
                        <div className="ui-heading text-[11px] uppercase tracking-widest text-cobalt">/ recommended export preset</div>
                        <h3 className="font-display font-bold tracking-tight text-xl mt-1">{preset.label}</h3>
                        <p className="text-sm text-ink-soft mt-1">{preset.tagline}</p>
                        <Link to={`/save-csv-for/${preset.id}`} className="btn-brutal inline-flex items-center bg-cobalt text-white px-4 py-2 ui-heading text-sm font-semibold border border-cobalt mt-4" data-testid="ask-preset-cta-btn">Learn more about this preset →</Link>
                    </div>
                )}

                <div className="mt-12 border-t border-ink/10 pt-8">
                    <h2 className="font-display font-black tracking-tighter text-2xl">Fix it yourself in 30 seconds.</h2>
                    <p className="text-sm text-ink-soft mt-2">OpenCSV opens, edits, and exports your CSV in the format your destination tool actually wants. Free to view, $9.99/mo Pro to edit & export.</p>
                    <Link to="/app" className="btn-brutal mt-4 inline-flex items-center bg-ink text-white px-5 py-2.5 ui-heading text-sm font-semibold border border-ink" data-testid="ask-cta-app">Open the editor →</Link>
                </div>
            </article>
        </>
    );
}
